// ============================================================
// CONDITION MODIFIERS - pure functions mapping a target's active
// conditions to the numeric test/reaction penalties they impose
// per Deathwatch RAW (docs/rules-spec.md §9). Zero React deps.
//
// This is the SINGLE rule source consumed by both the Overview
// sheet tests (via useRollActions) and the defence chain
// reactions (via getAvailableDefenceSteps), gated by the GM
// house rule `houseRules.applyConditionModifiers`.
//
// Attacker-side to-hit modifiers are modelled by getAttackConditionModifiers: the attacker's own test penalties, the
// defender's posture (Prone, Defensive Stance, Running) and the attacker's attack-only posture (a runner's shot).
// ============================================================
import { condType, condDuration, parseCustomPen, customPenScopeLabel } from './conditions';
import { toHitExposure } from './exposures';
import { isImmuneTo } from './conditionEligibility';
import { parseDrainCode, drainUnitLabel, DRAIN_UNITS } from './statDrains';
import type { CharacteristicId } from './character/types';

/** The nine characteristic ids - a custom scope matching one targets that characteristic (and its skills). */
const CHARACTERISTIC_IDS = new Set<string>(['ws', 'bs', 'str', 'tgh', 'agi', 'int', 'per', 'wp', 'fel']);

// ── Public types ──────────────────────────────────────────────

/** Structural context: any object carrying conditions + immunity trait fields (Target satisfies this). */
export interface ConditionModContext {
  conditions: string[];
  /** The bearer's size, for an exposure's size shift (v8.349.0). */
  size?: string | null;
  /** The team tag, for an exposure's `@team` scope (v8.349.0). */
  teamTag?: string | null;
  /** The side, for `@hostile` / `@ally` (2026-10-23); absent = the tag. */
  sideTag?: string | null;
  /** Innate exposures from the sheet - Fear (X), the Black Carapace (v8.354.0). */
  exposures?: readonly string[];
  /** The arc THIS attack arrives on, from the map (v8.368.2) - a relation, written on the active target only. */
  arrivalArc?: string | null;
  /** The auras this combatant stands under (v8.368.8), gathered by proximity - read when THEY attack. */
  auras?: readonly string[];
  frenzied?: boolean;
  stuffOfNightmares?: boolean;
  astartes?: boolean;
  hardy?: boolean;
  fromBeyond?: boolean;
  undying?: boolean;
  machineAP?: number;
}

/** Identifies the test being rolled. `characteristic` is the char itself (characteristic test) or the skill's governing characteristic (skill test). */
export interface ConditionTestDomain {
  characteristic: CharacteristicId;
  skill?: string | null;
}

export interface ConditionModifier {
  /** Summed penalty (≤ 0). */
  total: number;
  /** Human labels, e.g. ['Stunned −20', 'Fatigue (2) −20']. */
  labels: string[];
}

/** getAttackConditionModifiers adds per-contribution entries for the narrative. */
export interface AttackConditionModifier extends ConditionModifier {
  entries: { label: string; value: number }[];
  /** The defender wears a `snap` exposure: no aiming at them. Present only when true. */
  noAim?: boolean;
}

/** GM rulings that change which conditions bite. */
export interface ConditionModOptions {
  /**
   * Immobilised imposes −30 WS and prevents Dodge.
   *
   * ON unless a table says otherwise, which is what it has always done
   * for a vehicle. It became worth a switch of its own when the rule
   * started reaching PEOPLE too - a snare that stops you dodging is a
   * far bigger thing for a combatant than for a tank.
   */
  immobilisedAffectsCombat?: boolean;
  /**
   * The helpless rules (cannot miss, damage rolled twice) reach RANGED
   * attacks too. Off by default: as written they are for melee - see
   * `engine/helpless`.
   */
  helplessRanged?: boolean;
}

const DEFAULT_OPTIONS: Required<ConditionModOptions> = { immobilisedAffectsCombat: true, helplessRanged: false };

/** Is this condition switched off by a GM ruling? */
function ruledOut(base: string, options: ConditionModOptions): boolean {
  // Sn is Immobilised-by-weapon; the same table ruling governs both.
  return (base === 'Immob' || base === 'Sn') && options.immobilisedAffectsCombat === false;
}

export interface ReactionState {
  dodge: { disabled: boolean; mod: number };
  parry: { disabled: boolean; mod: number };
  /** Condition names that hard-disable a reaction (e.g. ['Stunned']). */
  disableSources: string[];
}

// ── Rule table ────────────────────────────────────────────────

/** Skills that require sight - penalised by Blinded. */
const SIGHT_SKILLS = ['awareness', 'search', 'scrutiny', 'dodge'];

/** A rule matches a test domain by governing characteristic, skill id, or 'all'. */
type DomainMatcher = { scope: 'all' } | { chars?: CharacteristicId[]; skills?: string[] };

interface CondModRule {
  /** Base code as returned by `condType` (S, F, P, B, D, Immob). */
  code: string;
  label: string;
  /** Penalty magnitude (negative); scaled by level when `perLevel`. */
  value: number;
  perLevel?: boolean;
  match: DomainMatcher;
}

const RULES: CondModRule[] = [
  { code: 'S', label: 'Stunned', value: -20, match: { scope: 'all' } },
  { code: 'F', label: 'Fatigue', value: -10, perLevel: true, match: { scope: 'all' } },
  { code: 'P', label: 'Prone', value: -20, match: { chars: ['ws'] } },
  { code: 'B', label: 'Blinded', value: -30, match: { chars: ['ws', 'bs'], skills: SIGHT_SKILLS } },
  { code: 'D', label: 'Deafened', value: -20, match: { skills: ['awareness'] } },
  { code: 'Immob', label: 'Immobilised', value: -30, match: { chars: ['ws'] } },
  // Snared (v8.198.2): a weapon-applied Immobilised with a per-turn
  // escape test. Identical combat effect, distinct code so a GM-set
  // Immobilised never gains the escape roll.
  { code: 'Sn', label: 'Snared', value: -30, match: { chars: ['ws'] } },
  // Pinned (v8.198.4): snap fire only - rules-spec.md's −20 BS.
  { code: 'Pn', label: 'Pinned', value: -20, match: { chars: ['bs'] } },
  // Guarded Attack (v8.302.0): RAW gives +10 to ALL Dodge and Parry
  // attempts until the start of your next turn. One row, and it reaches
  // the defence chain, the defence ledger, the character sheet's Dodge
  // and Parry rows and a plain target's derived rows - every surface
  // that already routes through this table. It CANNOT leak onto the
  // attack roll, because getAttackConditionModifiers builds its domain
  // with `skill` undefined and this rule matches on skills alone.
  { code: 'Gd', label: 'Guarded Attack', value: 10, match: { skills: ['dodge', 'parry'] } },
];

/**
 * Base codes that hard-disable reactions (helpless / stunned).
 *
 * `Dy` and `K` joined the list in v8.187.0. Reaction availability is
 * decided HERE and nowhere else - `resolveReactionAvailability` reads
 * this and has no separate guard of its own - so until they did, a man
 * bleeding out on the floor could parry a chainsword and a corpse could
 * dodge a bolt round. Nothing else about being Dying softens an attack
 * either: `isDeadSkip` gates on `'K'`, so a dying target takes full
 * damage, full criticals and every weapon special.
 *
 * Ruled on by the GM, and it is a rules change for every table that has
 * ever had somebody bleed out - see decisions-log 2026-08-23. It sits
 * behind `applyConditionModifiers` like the rest of this module, so a
 * table that wants none of it already has the switch.
 */
// Ao (v8.303.0) is the odd one here: every other entry is something
// DONE TO you, while All-Out Attack is a price you chose to pay. It
// belongs anyway - resolveReactionAvailability reads this list and has
// no guard of its own, so anything that takes a reaction away must be
// in it or the rule does not exist.
const DISABLE_BOTH = ['S', 'U', 'Pa', 'Dy', 'K', 'Ao'];
const DISABLE_DODGE = ['Immob', 'Sn'];
// Disarming(X)'s Dsm: the guard is beaten aside - parry only (v8.287.0).
const DISABLE_PARRY = ['Dsm'];

const DISABLE_LABELS: Record<string, string> = {
  S: 'Stunned',
  U: 'Unconscious',
  Pa: 'Paralysed',
  Dy: 'Dying',
  K: 'Dead',
  Immob: 'Immobilised',
  Sn: 'Snared',
  Dsm: 'Disarmed',
  Ao: 'All-Out Attack',
};

// ── Helpers ───────────────────────────────────────────────────

/** Minus sign matching the labels used across roll narratives. */
function fmtSigned(n: number): string {
  return n >= 0 ? '+' + n : '−' + Math.abs(n);
}

function cap(s: string): string {
  return s.replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Spellings of the same condition that both occur in stored state.
 *
 * `Immob` is what the condition picker writes; `Immobilised`, spelled
 * out, is what `weaponEffects.ts` pushes when a weapon snares somebody.
 * `conditions.ts` has carried both for months. Everything in this module
 * funnels through `baseCode`, so one entry here is enough to make a
 * snared combatant answer to the rules a vehicle already answered to.
 */
const CODE_ALIASES: Record<string, string> = {
  Immobilised: 'Immob',
};

/**
 * Which characteristic(s) each drain bends when a test is rolled. The
 * SCALE comes from `DRAIN_UNITS` (engine/statDrains.ts), so the chip,
 * the log and this modifier cannot drift apart: a bonus-typed drain is
 * worth ten test points, a characteristic-typed one is worth one.
 *
 * `wsbs` is one drain landing on two characteristics - Numbing dulls the
 * hand for blade and gun alike. `psy` is absent: a psy RATING is not a
 * characteristic and has no test - the projection overlay owns it.
 */
const DRAIN_TEST_CHARS: Record<string, string[] | undefined> = {
  str: ['str'],
  tgh: ['tgh'],
  wp: ['wp'],
  agi: ['agi'],
  wsbs: ['ws', 'bs'],
};

/** Base code letter for immunity/disable lookups (Cr:Left Leg → 'Cr'). */
function baseCode(code: string): string {
  if (code.startsWith('Cr:')) return 'Cr';
  if (code.startsWith('De:')) return 'De';
  const type = condType(code);
  return CODE_ALIASES[type] ?? type;
}

/** Whether an active trait grants immunity to the given base condition code. */
function isImmuneToCondition(ctx: ConditionModContext, base: string): boolean {
  // Delegates to the canonical predicate - two copies of this rule would
  // drift, and the Fire gap was exactly that kind of drift.
  return isImmuneTo(ctx as unknown as Record<string, unknown>, base);
}

interface ModInstance {
  label: string;
  value: number;
  match: DomainMatcher;
  /** Overrides the scope text in the summary - '' hides it (custom mods carry the scope in their label). */
  scopeLabel?: string;
}

/**
 * Stat drains (v8.294.0): THE one mechanism that reaches every test -
 * the character sheet's rows, the plain-target rows, the defence chain's
 * dodge/parry and the drained combatant's own attack roll. The Target
 * overlay deliberately no longer touches these stats; two subtractions
 * for one drain is the double-count this replaces.
 */
function expandDrain(code: string): ModInstance[] {
  const drain = parseDrainCode(code);
  if (!drain || drain.amount <= 0) return [];
  const chars = DRAIN_TEST_CHARS[drain.char];
  if (!chars) return []; // psy is not a characteristic - the overlay owns it
  const value = -drain.amount * (DRAIN_UNITS[drain.char]?.testPerPoint ?? 1);
  if (value === 0) return [];
  const label = `${drainUnitLabel(drain.char)} drained`;
  return chars.map((c) => ({ label, value, match: { chars: [c as CharacteristicId] } }));
}

/** A crippled limb: a leg costs Dodge and melee; an arm, two-handed work. */
function expandCrippled(code: string): ModInstance[] {
  const loc = cap(code.slice(3));
  if (/leg/i.test(loc))
    return [
      { label: `Crippled ${loc} (Dodge)`, value: -20, match: { skills: ['dodge'] } },
      { label: `Crippled ${loc} (melee)`, value: -20, match: { chars: ['ws'] } },
    ];
  // RAW is "−30 to TWO-HANDED actions", and the app has no concept of a
  // two-handed weapon - no field, no special, nothing to test. So this
  // applies the penalty to every WS test, which over-penalises a
  // one-handed swing. The label says so rather than implying precision
  // the number does not have; fixing it properly means adding a weapon
  // property, which is a feature and a rules change.
  if (/arm/i.test(loc)) return [{ label: `Crippled ${loc} (two-handed)`, value: -30, match: { chars: ['ws'] } }];
  return [];
}

/** What a custom modifier's scope reaches: every test, a characteristic (and its skills), or one skill. */
function penScopeMatcher(scope: string): DomainMatcher {
  if (scope === 'all') return { scope: 'all' };
  return CHARACTERISTIC_IDS.has(scope) ? { chars: [scope as CharacteristicId] } : { skills: [scope] };
}

/**
 * Structured custom modifier: Pen:<scope>=<value>[:<rounds>]. One entry
 * per scope, all carrying the same value: "−20 Dodge and Movement" is one
 * penalty that lands in two places, and the movement half is read by
 * `conditionMovement.ts` rather than here.
 */
function expandPen(code: string): ModInstance[] {
  const pen = parseCustomPen(code);
  if (!pen || pen.value === 0) return [];
  return pen.scopes.map((scope) => ({
    label: customPenScopeLabel(scope),
    value: pen.value,
    match: penScopeMatcher(scope),
    scopeLabel: '',
  }));
}

/** A catalog condition, through the rule table (Fatigue scales by level). */
function expandRule(code: string): ModInstance[] {
  const rule = RULES.find((r) => r.code === baseCode(code));
  if (!rule) return [];
  if (!rule.perLevel) return [{ label: rule.label, value: rule.value, match: rule.match }];
  const level = condDuration(code) || 1;
  return [{ label: `${rule.label} (${level})`, value: rule.value * level, match: rule.match }];
}

/** The code families with their own expansion; anything else goes through the rule table. */
const PREFIX_EXPANDERS: readonly [string, (code: string) => ModInstance[]][] = [
  ['Dn:', expandDrain],
  ['Cr:', expandCrippled],
  ['Pen:', expandPen],
];

/** Expand a stored condition code into its applicable modifier instances. */
function expandCondition(code: string): ModInstance[] {
  const expander = PREFIX_EXPANDERS.find(([prefix]) => code.startsWith(prefix));
  return expander ? expander[1](code) : expandRule(code);
}

/** True when a condition counts: its bearer is not immune and the table has not ruled it out. */
function counts(ctx: ConditionModContext, code: string, options: ConditionModOptions): boolean {
  const base = baseCode(code);
  return !isImmuneToCondition(ctx, base) && !ruledOut(base, options);
}

/** Every modifier instance the bearer's counting conditions carry, in condition order. */
function liveInstances(
  ctx: ConditionModContext,
  codes: readonly string[],
  options: ConditionModOptions,
): ModInstance[] {
  return codes.filter((code) => counts(ctx, code, options)).flatMap(expandCondition);
}

function matches(m: DomainMatcher, d: ConditionTestDomain): boolean {
  if ('scope' in m) return m.scope === 'all';
  if (m.chars && m.chars.includes(d.characteristic)) return true;
  if (m.skills && d.skill != null && m.skills.includes(d.skill)) return true;
  return false;
}

/** Human description of what a matcher affects, for the summary card. */
function describeScope(m: DomainMatcher): string {
  if ('scope' in m) return 'all tests';
  const parts: string[] = [];
  if (m.chars) parts.push(...m.chars.map((c) => c.toUpperCase()));
  if (m.skills) parts.push(...m.skills.map(cap));
  return parts.join(', ');
}

// ── Core API ──────────────────────────────────────────────────

/**
 * Total condition-derived modifier for a single test domain, plus the
 * labels of the contributing conditions (immune conditions excluded).
 */
export function getConditionTestModifier(
  ctx: ConditionModContext,
  domain: ConditionTestDomain,
  options: ConditionModOptions = DEFAULT_OPTIONS,
): ConditionModifier {
  const hits = liveInstances(ctx, ctx.conditions, options).filter((inst) => matches(inst.match, domain));
  return {
    total: hits.reduce((sum, inst) => sum + inst.value, 0),
    labels: hits.map((inst) => `${inst.label} ${fmtSigned(inst.value)}`),
  };
}

/**
 * Defender STATE that modifies the ATTACKER's to-hit rather than any test
 * the defender rolls. RAW: a prone target is easier to hit in melee and
 * harder at range.
 */
const DEFENDER_POSTURE_RULES: { code: string; label: string; melee: number; ranged: number }[] = [
  { code: 'P', label: 'Target Prone', melee: 10, ranged: -10 },
  // RAW Defensive Stance: everyone attacking the stance-taker suffers
  // -20 WS. Applied to ranged too by table convention here? No - RAW
  // names WS tests only, so ranged is untouched (v8.214.0 ruling: the
  // book text governs; snipers do not care about your footwork).
  { code: 'Ds', label: 'Target Defensive Stance', melee: -20, ranged: 0 },
  // A runner (2026-10-26): the rules-spec's "Target is Running -20" at range, and the book's +20 to strike him in
  // melee (the user's ruling, 2026-10-24).
  { code: 'Rn', label: 'Target Running', melee: 20, ranged: -20 },
];

/**
 * The ATTACKER's state that modifies only his ATTACK, never his other tests: a runner shoots at -20 (and full auto
 * only - the gate refuses the rest, hooks/useShellWeaponGates). Kept apart from RULES, which apply to every test
 * of the characteristic, sheet tests included.
 */
const ATTACKER_POSTURE_RULES: { code: string; label: string; melee: number; ranged: number }[] = [
  { code: 'Rn', label: 'Running', melee: 0, ranged: -20 },
];

/**
 * Every condition-derived modifier on an attack roll: the attacker's own
 * conditions applied to the governing characteristic (WS in melee, BS at
 * range), plus the defender's posture.
 *
 * Routed through the SAME rule table as every other consumer - per the
 * standing rule in dev-docs/gotchas.md, a new consumer that recomputes
 * penalties will drift from the forecast engines. So Stunned −20,
 * Fatigue −10/level, Blinded −30 and Immobilised −30 (WS) all apply to
 * the attacker for free, immunity handling included.
 */
export function getAttackConditionModifiers(
  attacker: ConditionModContext,
  defender: ConditionModContext,
  isMelee: boolean,
  options: ConditionModOptions = DEFAULT_OPTIONS,
  /** The attack itself, for an exposure's range shift (v8.349.0) and its charge axis (v8.368.2). */
  attack: { rangeBand?: string | null; charge?: boolean } = {},
): AttackConditionModifier {
  const own = [...attackerEntries(attacker, isMelee, options), ...postureEntries(attacker, isMelee, options, ATTACKER_POSTURE_RULES)];
  const posture = postureEntries(defender, isMelee, options);
  const exposure = defenderExposure(attacker, defender, isMelee, attack);
  const entries = [...own, ...posture, ...exposure.entries];
  const total = sumValues(own) + sumValues(posture) + exposure.total;
  return {
    total,
    labels: entries.map((e) => `${e.label} ${fmtSigned(e.value)}`),
    entries,
    ...(exposure.noAim ? { noAim: true } : {}),
  };
}

type Entry = { label: string; value: number };

const sumValues = (entries: readonly Entry[]): number => entries.reduce((sum, e) => sum + e.value, 0);

/**
 * The attacker's own conditions, against the governing characteristic.
 * PLAIN labels: every renderer (ledger chip, breakdown row) appends the
 * value itself - an embedded one printed twice ('Target Prone +10 +10',
 * v8.237.4). The prose `labels` carry the signed form for narrative
 * sentences.
 */
function attackerEntries(attacker: ConditionModContext, isMelee: boolean, options: ConditionModOptions): Entry[] {
  const domain: ConditionTestDomain = { characteristic: isMelee ? 'ws' : 'bs' };
  return liveInstances(attacker, attacker.conditions ?? [], options)
    .filter((inst) => matches(inst.match, domain))
    .map((inst) => ({ label: inst.label, value: inst.value }));
}

/** One defender condition's posture entry for this attack, or null when it has none. */
function postureEntry(
  defender: ConditionModContext,
  code: string,
  isMelee: boolean,
  options: ConditionModOptions,
  rules = DEFENDER_POSTURE_RULES,
) {
  const rule = rules.find((r) => r.code === baseCode(code));
  if (!rule || !counts(defender, code, options)) return null;
  const value = isMelee ? rule.melee : rule.ranged;
  return value ? { label: rule.label, value } : null;
}

/**
 * A figure's posture entries under one table: the DEFENDER's (which modify the attacker rather than him) by
 * default, or the attacker's own that touch only this attack (a runner's shot, ATTACKER_POSTURE_RULES).
 */
function postureEntries(
  figure: ConditionModContext,
  isMelee: boolean,
  options: ConditionModOptions,
  rules = DEFENDER_POSTURE_RULES,
): Entry[] {
  return (figure.conditions ?? [])
    .map((code) => postureEntry(figure, code, isMelee, options, rules))
    .filter((e): e is Entry => e !== null);
}

/**
 * THE DEFENDER'S EXPOSURES (v8.349.0): what they wear that changes how
 * THIS attacker hits them - Painted, Shrouded, a size or range shift -
 * one named entry each, through the same funnel, so the roll, the ledger
 * and both forecasts agree by construction.
 */
function defenderExposure(
  attacker: ConditionModContext,
  defender: ConditionModContext,
  isMelee: boolean,
  attack: { rangeBand?: string | null; charge?: boolean },
) {
  return toHitExposure(
    {
      conditions: defender.conditions,
      exposures: defender.exposures,
      teamTag: defender.teamTag,
      sideTag: defender.sideTag,
      size: defender.size,
      arc: defender.arrivalArc ?? null,
    },
    {
      teamTag: attacker.teamTag,
      sideTag: attacker.sideTag,
      isMelee,
      rangeBand: attack.rangeBand,
      charge: !!attack.charge,
      auras: attacker.auras,
    },
  );
}

/**
 * Human-readable list of every active condition modifier, e.g.
 * ['Stunned −20 (all tests)', 'Prone −20 (WS)'] - for the Overview summary.
 * Immune conditions are excluded.
 */
export function describeConditionModifiers(
  ctx: ConditionModContext,
  options: ConditionModOptions = DEFAULT_OPTIONS,
): string[] {
  return liveInstances(ctx, ctx.conditions, options).map(describeInstance);
}

/** One instance for the summary: its signed value, and what it reaches in parentheses. */
function describeInstance(inst: ModInstance): string {
  // Custom mods carry the scope in their label (scopeLabel:'') - no parens.
  const scope = inst.scopeLabel !== undefined ? inst.scopeLabel : describeScope(inst.match);
  const text = `${inst.label} ${fmtSigned(inst.value)}`;
  return scope ? `${text} (${scope})` : text;
}

/**
 * Dodge/parry reaction state: numeric modifier plus hard-disable gates
 * (Stunned/Unconscious/Paralysed disable both; Immobilised disables dodge).
 */
export function getConditionReactionState(
  ctx: ConditionModContext,
  options: ConditionModOptions = DEFAULT_OPTIONS,
): ReactionState {
  const dodgeMod = getConditionTestModifier(ctx, { characteristic: 'agi', skill: 'dodge' }, options).total;
  const parryMod = getConditionTestModifier(ctx, { characteristic: 'ws', skill: 'parry' }, options).total;
  const { dodgeDisabled, parryDisabled, disableSources } = reactionGates(ctx, options);
  return {
    dodge: { disabled: dodgeDisabled, mod: dodgeMod },
    parry: { disabled: parryDisabled, mod: parryMod },
    disableSources,
  };
}

/** Append `item` unless the list already has it. */
function addOnce(list: string[], item: string): void {
  if (!list.includes(item)) list.push(item);
}

/** The reactions the bearer's counting conditions take away, and the names of those conditions. */
function reactionGates(ctx: ConditionModContext, options: ConditionModOptions) {
  let dodgeDisabled = false;
  let parryDisabled = false;
  const disableSources: string[] = [];
  for (const code of ctx.conditions.filter((c) => counts(ctx, c, options))) {
    const base = baseCode(code);
    const gate = reactionGate(base);
    if (!gate) continue;
    dodgeDisabled ||= gate.dodge;
    parryDisabled ||= gate.parry;
    addOnce(disableSources, DISABLE_LABELS[base]);
  }
  return { dodgeDisabled, parryDisabled, disableSources };
}

/** Which reactions a condition takes away, or null when it takes none. */
function reactionGate(base: string): { dodge: boolean; parry: boolean } | null {
  if (DISABLE_BOTH.includes(base)) return { dodge: true, parry: true };
  if (DISABLE_DODGE.includes(base)) return { dodge: true, parry: false };
  return DISABLE_PARRY.includes(base) ? { dodge: false, parry: true } : null;
}
